import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Text } from './ui';

const formatWeekRange = (weekStart) => {
    if (!weekStart) return 'Unknown';
    const start = new Date(weekStart);
    if (Number.isNaN(start.getTime())) return 'Unknown';
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    const options = { month: 'short', day: 'numeric' };
    return `${start.toLocaleDateString('en-US', options)} – ${end.toLocaleDateString('en-US', options)}`;
};

const formatNumber = (value) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) return '0';
    return new Intl.NumberFormat('en-US').format(Number(value));
};

// Enhanced smoothing function using weighted moving average with cubic interpolation
const smoothData = (data, windowSize = 2) => {
    if (data.length <= 2) return data;
    
    // Apply weighted moving average with cubic smoothing
    return data.map((item, index) => {
        const weights = [0.1, 0.2, 0.4, 0.2, 0.1]; // Center-weighted smoothing
        let totalWeight = 0;
        let startedSum = 0;
        let completedSum = 0;
        
        // Apply weighted smoothing
        for (let i = 0; i < weights.length; i++) {
            const offset = i - Math.floor(weights.length / 2);
            const dataIndex = index + offset;
            
            if (dataIndex >= 0 && dataIndex < data.length) {
                const weight = weights[i];
                totalWeight += weight;
                startedSum += (data[dataIndex].sessions_started || 0) * weight;
                completedSum += (data[dataIndex].sessions_completed || 0) * weight;
            }
        }
        
        return {
            ...item,
            sessions_started: Math.round(startedSum / totalWeight),
            sessions_completed: Math.round(completedSum / totalWeight)
        };
    });
};

const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        return (
            <div className="bg-white border border-neutral-200 rounded-lg p-3 shadow-lg">
                <Text className="text-neutral-900 font-medium text-sm mb-2">
                    {formatWeekRange(label)}
                </Text>
                {payload.map((entry, index) => (
                    <div key={index} className="flex items-center gap-2 mb-1">
                        <div 
                            className="w-3 h-3 rounded-full" 
                            style={{ backgroundColor: entry.color }}
                        />
                        <Text className="text-neutral-900 text-sm">
                            {entry.name}: {formatNumber(entry.value)}
                        </Text>
                    </div>
                ))}
            </div>
        );
    }
    return null;
};

const SessionTimelineChart = ({ data = [] }) => {
    if (!data.length) {
        return (
            <div className="h-64 flex items-center justify-center bg-neutral-50 hairline">
                <Text className="text-neutral-900 opacity-60 text-sm font-mono uppercase tracking-wider">
                    No session timeline data yet
                </Text>
            </div>
        );
    }

    // Process data for the chart
    const chartData = data.map(item => ({
        ...item,
        week_display: formatWeekRange(item.week_start),
        sessions_started: Number(item.sessions_started || 0),
        sessions_completed: Number(item.sessions_completed || 0)
    }));

    // Apply smoothing
    const smoothedData = smoothData(chartData);

    return (
        <div className="space-y-4">
            <div className="h-80 w-full">
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                        data={smoothedData}
                        margin={{
                            top: 20,
                            right: 30,
                            left: 20,
                            bottom: 20,
                        }}
                    >
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                        <XAxis 
                            dataKey="week_display"
                            stroke="#666"
                            fontSize={12}
                            angle={-45}
                            textAnchor="end"
                            height={80}
                        />
                        <YAxis 
                            stroke="#666"
                            fontSize={12}
                            tickFormatter={formatNumber}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Line
                            type="monotone"
                            dataKey="sessions_started"
                            stroke="#1b263b"
                            strokeWidth={3}
                            dot={{ fill: '#1b263b', strokeWidth: 2, r: 4 }}
                            name="Sessions Started"
                            connectNulls={false}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                        <Line
                            type="monotone"
                            dataKey="sessions_completed"
                            stroke="#059669"
                            strokeWidth={2}
                            dot={{ fill: '#059669', strokeWidth: 2, r: 3 }}
                            name="Sessions Completed"
                            connectNulls={false}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </LineChart>
                </ResponsiveContainer>
            </div>
            
            <div className="flex items-center justify-between text-xs text-neutral-500">
                <Text className="font-mono uppercase tracking-wider">
                    Showing up to the last six weeks of activity
                </Text>
                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-oxfordBlue"></div>
                        <Text className="font-mono uppercase tracking-wider">Started</Text>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-emerald-600"></div>
                        <Text className="font-mono uppercase tracking-wider">Completed</Text>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SessionTimelineChart;
